import { SvgTrashCan } from '@cocoonspace/icons/react/font-awesome/light/trash-can'
import { SvgArrowUpFromBracket } from '@cocoonspace/icons/react/font-awesome/regular/arrow-up-from-bracket'
import type { Event } from '@cocoonspace/sdk-js/types/event'
import { AlertTriangle, ArrowRight } from 'lucide-react'
import { useTranslation } from 'next-i18next'
import Link from 'next/link'
import type { ComponentProps } from 'react'
import { Price } from '~/components/price'
import { Alert, AlertTitle } from '~/components/ui/alert'
import { buttonVariants } from '~/components/ui/button'
import { Card, cardVariants } from '~/components/ui/card'
import { Skeleton } from '~/components/ui/skeleton'
import { EventAccessDoorCode } from '~/domains/events/components/atoms/event-access-door-code'
import { EventCancellationConfirmDialog } from '~/domains/events/components/event-cancellation-confirm-dialog'
import { EventDateTimeInfos } from '~/domains/events/components/molecules/event-date-time-infos'
import { EventShareDialog } from '~/domains/events/components/organisms/event-share-dialog'
import { SpaceFullAddress } from '~/domains/spaces/components/atoms/space-full-address'
import { useExperiment } from '~/hooks/use-experiment'
import { cn } from '~/lib/utils'
import { useTracking } from '~/providers/tracking-provider'
import { useUserContext } from '~/providers/user-provider'
import { EventCardFooter } from './event-card-footer'
import { EventCardSpacePreview } from './event-card-space-preview'

const OverstayAlert = ({ eventId }: { eventId: string }) => {
	const { t } = useTranslation()
	const { accountQuery } = useUserContext()

	return (
		<Alert variant='destructive'>
			<AlertTriangle size={16} />

			<AlertTitle className='mb-0 flex w-full flex-1 flex-row items-center'>
				{t('eventDetails:overstay.spotted')}

				{!accountQuery.data?.business && (
					<Link
						href={{
							pathname: '/overstays/[eventId]',
							query: { eventId },
						}}
						className={buttonVariants({
							variant: 'textPrimary',
							size: 'sm',
							className: 'ml-auto gap-2 rounded-md',
						})}
					>
						{t('eventDetails:overstay.fixOverstay')}

						<ArrowRight size={16} />
					</Link>
				)}
			</AlertTitle>
		</Alert>
	)
}

const CancelBtn = ({ event }: { event: Event }) => {
	const { track } = useTracking()

	return (
		<EventCancellationConfirmDialog event={event}>
			<button
				type='button'
				className='absolute top-6 right-4 z-10 mr-1 text-primary'
				onClick={() => track.click('eventCard-delete-button')}
			>
				<SvgTrashCan />
			</button>
		</EventCancellationConfirmDialog>
	)
}

const AddressAndPrice = ({
	event,
	isPast,
	showPrice,
}: {
	event: Event
	isPast?: boolean
	showPrice?: boolean
}) => {
	const { t } = useTranslation()

	return (
		<div className='flex items-center justify-between gap-4'>
			<div className='truncate'>
				{isPast ? (
					t(`myEvents:status.${event.status}`)
				) : (
					<SpaceFullAddress
						space={event.space!}
						className='text-sm'
					/>
				)}
			</div>

			{showPrice && event.type !== 'quotation' && (
				<Price
					price={event.total_price}
					taxType='ht'
					className='text-lg'
					taxClassName='mb-[5px]'
				/>
			)}
		</div>
	)
}

export const EventCard = ({
	event,
	isFeatured = false,
	isPast = false,
}: {
	event: Event
	isFeatured?: boolean
	isPast?: boolean
}) => {
	const { track } = useTracking()
	const hasOverstayExperiment = useExperiment('overstay')

	return (
		<div
			className={cn('relative flex flex-col', {
				'md:w-[700px]': isFeatured,
			})}
		>
			{!isFeatured && event.cancellable && <CancelBtn event={event} />}

			{isFeatured && (
				<EventShareDialog event={event}>
					<button
						type='button'
						className='absolute top-6 right-6 z-10'
						onClick={() => track.click('featuredEvent-share-button')}
					>
						<SvgArrowUpFromBracket className='mr-1 text-primary' />
					</button>
				</EventShareDialog>
			)}

			<Link
				href={`/event/${event.id}`}
				className={cn(cardVariants(), 'flex flex-1 flex-col gap-4 p-4', {
					'md:flex-row': isFeatured,
				})}
				shallow
				onClick={() => track.click('eventCard')}
			>
				<EventCardSpacePreview
					event={event}
					className={cn('hidden w-[300px]', { 'md:block': isFeatured })}
				/>

				<div className='flex min-w-0 flex-1 flex-col gap-4'>
					<EventDateTimeInfos
						event={event}
						className='pr-4'
					/>

					<div>
						<EventCardSpacePreview
							event={event}
							className={cn({ 'md:hidden': isFeatured })}
						/>

						<AddressAndPrice
							event={event}
							isPast={isPast}
							showPrice={!isFeatured}
						/>
					</div>

					{isFeatured && <EventAccessDoorCode event={event} />}

					{event.overstay?.status === 'payment_required' &&
					hasOverstayExperiment ? (
						<OverstayAlert eventId={event.id} />
					) : (
						<EventCardFooter
							isPast={isPast}
							event={event}
						/>
					)}
				</div>
			</Link>
		</div>
	)
}

EventCard.skeleton = (props: ComponentProps<'div'>) => (
	<Card className={cn('relative max-w-[352px]', props.className)}>
		<EventDateTimeInfos.skeleton className='mb-4' />

		<EventCardSpacePreview.skeleton />

		<Skeleton className='mb-4 h-[16px] w-[200px]' />

		<EventCardFooter.skeleton />
	</Card>
)

export default EventCard
